import { WorkerCalcPayload, WorkerCalcResult } from './indicatorWorker';
import {
  calculateSMA,
  calculateEMA,
  calculateRSI,
  calculateMACD,
  calculateStochastic,
  calculateVolumeHistogram,
  calculateBuySellFrequency,
  calculateForeignDomestic,
  calculateDailyShio,
  calculateDailyElement,
  calculateBaZiIndicator
} from '../indicators';

class IndicatorWorkerManager {
  private worker: Worker | null = null;
  private pendingRequests = new Map<string, {
    resolve: (result: Record<string, any>) => void;
    reject: (reason: any) => void;
  }>();

  constructor() {
    this.initWorker();
  }

  private initWorker() {
    if (typeof window === 'undefined' || typeof Worker === 'undefined') {
      return;
    }

    try {
      this.worker = new Worker(
        new URL('./indicatorWorker.ts', import.meta.url),
        { type: 'module' }
      );

      this.worker.onmessage = (event: MessageEvent<WorkerCalcResult>) => {
        const { requestId, results, error } = event.data;
        const pending = this.pendingRequests.get(requestId);
        if (pending) {
          this.pendingRequests.delete(requestId);
          if (error) {
            console.warn('Worker computation returned error, falling back:', error);
            pending.reject(new Error(error));
          } else {
            pending.resolve(results);
          }
        }
      };

      this.worker.onerror = (err) => {
        console.warn('Worker encountered an error:', err);
      };
    } catch (e) {
      console.warn('Failed to initialize Indicator Worker, using main-thread fallback:', e);
      this.worker = null;
    }
  }

  public async computeIndicators(payload: Omit<WorkerCalcPayload, 'requestId'>): Promise<Record<string, any>> {
    const requestId = `req_${Date.now()}_${Math.random().toString(36).substring(2, 7)}`;

    if (this.worker) {
      return new Promise((resolve, _reject) => {
        // Set fallback timeout in case worker hangs
        const timeout = setTimeout(() => {
          if (this.pendingRequests.has(requestId)) {
            this.pendingRequests.delete(requestId);
            console.warn('Indicator Worker timed out, computing on main thread fallback');
            resolve(this.computeOnMainThread(payload));
          }
        }, 5000);

        this.pendingRequests.set(requestId, {
          resolve: (res) => {
            clearTimeout(timeout);
            resolve(res);
          },
          reject: (_err) => {
            clearTimeout(timeout);
            console.warn('Worker rejected, computing on main thread fallback');
            resolve(this.computeOnMainThread(payload));
          }
        });

        this.worker!.postMessage({ ...payload, requestId });
      });
    }

    return this.computeOnMainThread(payload);
  }

  private computeOnMainThread(payload: Omit<WorkerCalcPayload, 'requestId'>): Record<string, any> {
    const { filteredRows, bidAskData = [], foreignFlowData = [], timeframe, indicators } = payload;
    const results: Record<string, any> = {};

    for (const indicator of indicators) {
      if (!indicator.enabled) continue;

      const type = indicator.type;
      const period = indicator.period || 14;

      switch (type) {
        case 'ma':
        case 'sma':
        case 'ema': {
          const mode = indicator.maMode || (type === 'ema' ? 'exponential' : 'simple');
          if (mode === 'exponential') {
            results[indicator.id] = { data: calculateEMA(filteredRows, period) };
          } else {
            results[indicator.id] = { data: calculateSMA(filteredRows, period) };
          }
          break;
        }

        case 'rsi': {
          results[indicator.id] = { data: calculateRSI(filteredRows, period) };
          break;
        }

        case 'macd': {
          const macdCalc = calculateMACD(filteredRows, 12, 26, 9);
          results[indicator.id] = {
            data: macdCalc.macd,
            macd: macdCalc.macd,
            signal: macdCalc.signal,
            histogram: macdCalc.histogram
          };
          break;
        }

        case 'stochastic': {
          const stochCalc = calculateStochastic(filteredRows, period, 3);
          results[indicator.id] = {
            data: stochCalc.k,
            k: stochCalc.k,
            d: stochCalc.d
          };
          break;
        }

        case 'volume_histogram': {
          results[indicator.id] = { data: calculateVolumeHistogram(filteredRows) };
          break;
        }

        case 'bazi_indicator':
        case 'bazi_daily':
        case 'bazi_monthly': {
          const mode = indicator.type === 'bazi_monthly' ? 'monthly' : 'daily';
          const baziCalc = calculateBaZiIndicator(filteredRows, timeframe, mode);
          results[indicator.id] = {
            data: baziCalc.data,
            mainElementData: baziCalc.mainElementData,
            shioElementData: baziCalc.shioElementData,
            textItems: baziCalc.textItems
          };
          break;
        }

        case 'buy_sell_frequency': {
          const buySellCalc = calculateBuySellFrequency(filteredRows, bidAskData, period);
          results[indicator.id] = {
            data: buySellCalc.buyFreq,
            buyFreq: buySellCalc.buyFreq,
            sellFreq: buySellCalc.sellFreq
          };
          break;
        }

        case 'foreign_domestic': {
          results[indicator.id] = { data: calculateForeignDomestic(filteredRows, foreignFlowData) };
          break;
        }

        case 'daily_shio': {
          const shioCalc = calculateDailyShio(filteredRows, timeframe);
          results[indicator.id] = {
            data: shioCalc.data,
            labels: shioCalc.labels,
            colors: shioCalc.colors
          };
          break;
        }

        case 'daily_element': {
          const elementCalc = calculateDailyElement(filteredRows, timeframe);
          results[indicator.id] = {
            data: elementCalc.data,
            labels: elementCalc.labels,
            colors: elementCalc.colors
          };
          break;
        }

        default:
          break;
      }
    }

    return results;
  }

  public terminate() {
    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }
  }
}

export const indicatorWorkerManager = new IndicatorWorkerManager();
